import { API_URL } from 'config';
import { makeAPICall } from 'models/api';

export declare namespace API {
    export interface FaftPrioritisation {}

    export interface FaftPrioritisationTask {
        id: number;
        plan_id: string;
        bfmc_id: number;
        retry_count: number;
        error_count: number;
        status: string;
        submitted_by: string;
        submitted_at: string;
        started_at: string;
    }

    export interface FaftPrioritisationTaskErrors {
        id: number;
        task_id: string;
        retry_count: number;
        error_details: string;
    }

    export interface FaftPrioritisationStatistics {
        plan_id: string;
        bfmc_id: number;
        faft_id: string;
        priority_focus_areas: string;
        priority_ignition_history: string;
        priority_sfpp_proximity: string;
        priority_residential_proximity: string;
        priority: string;
        priority_calculated: string;
        priority_override_justification: string;
        priority_justification_history: string;

        complexity_soil_erosion_risk: string;
        complexity_tenure: string;
        complexity_slope: string;
        complexity_water_body: string;
        complexity_cultural_sensitivities: string;
        complexity_environmental_sensitivities: string;
        complexity: string;
        complexity_calculated: string;
        complexity_override_justification: string;
        complexity_justification_history: string;
    }

    export interface FaftPrioritisationSummaryStatistics {
        id: number;
        plan_id: string;
        faft_id: string;
        fire_trail_name: string;
        priority: string;
        complexity: string;
        year1work_types: string;
        year2work_types: string;
        year3work_types: string;
        year4work_types: string;
        year5work_types: string;
        processing_status: string;
        processing_errors: string;
    }

    export interface ActionResponse {
        success: boolean;
        error?: string;
    }
}

export interface FaftPrioritisationTask {
    id: number;
    planId: string;
    bfmcId: number;
    retryCount: number;
    errorCount: number;
    status: string;
    submittedBy: string;
    submittedAt: string;
    startedAt: string;
}

export interface FaftPrioritisationStatistics {
    planId: string;
    bfmcId: number;
    faftId: string;

    priority: string;
    priorityCalculated: string;
    priorityFocusAreas: string;
    priorityIgnitionHistory: string;
    prioritySfppProximity: string;
    priorityResidentialProximity: string;
    priorityJustification: string;
    priorityJustificationHistory: string;

    complexity: string;
    complexityCalculated: string;
    complexitySoilErosionRisk: string;
    complexityTenure: string;
    complexitySlope: string;
    complexityWaterBody: string;
    complexityCulturalSensitivities: string;
    complexityEnvironmentalSensitivities: string;
    complexityJustification: string;
    complexityJustificationHistory: string;
}

export const mapAPIToFaftPrioritisation = ({
    plan_id,
    bfmc_id,
    faft_id,
    priority,
    priority_calculated,
    priority_focus_areas,
    priority_ignition_history,
    priority_sfpp_proximity,
    priority_residential_proximity,
    priority_override_justification,
    priority_justification_history,
    complexity,
    complexity_calculated,
    complexity_soil_erosion_risk,
    complexity_tenure,
    complexity_slope,
    complexity_water_body,
    complexity_cultural_sensitivities,
    complexity_environmental_sensitivities,
    complexity_override_justification,
    complexity_justification_history,
    ...rest
}: API.FaftPrioritisationStatistics): FaftPrioritisationStatistics => ({
    ...rest,
    planId: plan_id,
    bfmcId: bfmc_id,
    faftId: faft_id,
    priority: priority,
    priorityCalculated: priority_calculated,
    priorityFocusAreas: priority_focus_areas,
    priorityIgnitionHistory: priority_ignition_history,
    prioritySfppProximity: priority_sfpp_proximity,
    priorityResidentialProximity: priority_residential_proximity,
    priorityJustification: priority_override_justification,
    priorityJustificationHistory: priority_justification_history,
    complexity: complexity,
    complexityCalculated: complexity_calculated,
    complexitySoilErosionRisk: complexity_soil_erosion_risk,
    complexityTenure: complexity_tenure,
    complexitySlope: complexity_slope,
    complexityWaterBody: complexity_water_body,
    complexityCulturalSensitivities: complexity_cultural_sensitivities,
    complexityEnvironmentalSensitivities:
        complexity_environmental_sensitivities,
    complexityJustification: complexity_override_justification,
    complexityJustificationHistory: complexity_justification_history,
});

export const mapAPIToFaftPrioritisationTask = ({
    plan_id,
    bfmc_id,
    retry_count,
    error_count,
    submitted_by,
    submitted_at,
    started_at,
    ...rest
}: API.FaftPrioritisationTask): FaftPrioritisationTask => ({
    ...rest,
    planId: plan_id,
    bfmcId: bfmc_id,
    retryCount: retry_count,
    errorCount: error_count,
    submittedBy: submitted_by,
    submittedAt: submitted_at,
    startedAt: started_at,
});

export const mapAPIToFaftPrioritisationSummaryStatistics = ({
    id,
    plan_id,
    faft_id,
    fire_trail_name,
    complexity,
    priority,
    year1work_types,
    year2work_types,
    year3work_types,
    year4work_types,
    year5work_types,
    processing_status,
    processing_errors,
    ...rest
}: API.FaftPrioritisationSummaryStatistics): FaftPrioritisationSummaryStatistics => ({
    ...rest,
    id: id,
    planId: plan_id,
    faftId: faft_id,
    fireTrailName: fire_trail_name,
    priority: priority,
    complexity: complexity,
    year1WorkTypes: year1work_types,
    year2WorkTypes: year2work_types,
    year3WorkTypes: year3work_types,
    year4WorkTypes: year4work_types,
    year5WorkTypes: year5work_types,
    processingStatus: processing_status,
    processingErrors: processing_errors,
});

export const getFaftPrioritisationStatistics = makeAPICall<
    FaftPrioritisationStatistics | undefined,
    {
        planId: string;
        faftId: string;
    },
    API.FaftPrioritisationStatistics
>(
    ({ planId, faftId }) => {
        return {
            url: `${API_URL}/faft/prioritisation/${planId}/statistics/${faftId}`,
        };
    },
    (raw) => {
        if (raw) return mapAPIToFaftPrioritisation(raw);
    },
);

export const updateFaftPrioritisationStatistics = makeAPICall<
    FaftPrioritisationStatistics,
    {
        planId: string;
        faftId: string;
        prioritisationData: PrioritisationUpdate; // Assumes `WorkTypes` maps directly to the API model structure.
    },
    API.FaftPrioritisationStatistics
>(
    ({ planId, faftId, prioritisationData }) => {
        return {
            url: `${API_URL}/faft/prioritisation/${planId}/statistics/${faftId}`,
            requestData: {
                contentType: 'application/json',
                method: 'PUT',
                body: JSON.stringify(prioritisationData),
            },
        };
    },
    (raw) => mapAPIToFaftPrioritisation(raw),
);

export const getFaftPrioritisationTask = makeAPICall<
    FaftPrioritisationTask | undefined,
    {
        planId: string;
    },
    API.FaftPrioritisationTask
>(
    ({ planId }) => {
        return {
            url: `${API_URL}/faft/prioritisation/task/${planId}/latest`,
        };
    },
    (raw) => {
        if (raw) return mapAPIToFaftPrioritisationTask(raw);
    },
);

export interface FaftPrioritisationSummaryStatistics {
    id: number;
    planId: string;
    faftId: string;
    fireTrailName: string;
    priority: string;
    complexity: string;
    processingStatus: string;
    processingErrors: string;
    year1WorkTypes: string;
    year2WorkTypes: string;
    year3WorkTypes: string;
    year4WorkTypes: string;
    year5WorkTypes: string;
}

export const getFaftPrioritisationSummaryStatistics = makeAPICall<
    FaftPrioritisationSummaryStatistics[] | undefined,
    {
        planId: string;
    },
    API.FaftPrioritisationSummaryStatistics[]
>(
    ({ planId }) => {
        return {
            url: `${API_URL}/faft/prioritisation/${planId}/statistics`,
        };
    },
    (raw) => raw.map((fp) => mapAPIToFaftPrioritisationSummaryStatistics(fp)),
);

export const getExportUrl = (
    planId: string,
    exportType: 'priority' | 'complexity' | 'treatment',
) => {
    switch (exportType) {
        case 'priority': {
            return `${API_URL}/faft/prioritisation/${planId}/statistics/export/${exportType}`;
        }
        case 'complexity': {
            return `${API_URL}/faft/prioritisation/${planId}/statistics/export/${exportType}`;
        }
        case 'treatment': {
            return `${API_URL}/faft/prioritisation/${planId}/workregister/export/csv`;
        }
    }
};

export const getExportFilename = (
    exportType: 'priority' | 'complexity' | 'treatment',
) => {
    switch (exportType) {
        case 'priority': {
            return 'PriorityStatistics.csv';
        }
        case 'complexity': {
            return 'ComplexityStatistics.csv';
        }
        case 'treatment': {
            return 'Treatments.csv';
        }
    }
};

export interface PrioritisationUpdate {
    fireTrailId?: string;
    planId?: string;

    //complexity attributes
    complexity?: string;
    complexityCalculated?: string;
    complexityJustification?: string;
    soilErosionRisk?: string;
    tenure?: string;
    slope?: string;
    waterBody?: string;
    culturalSensitivities?: string;
    environmentalSensitivities?: string;
    complexityJustificationHistory?: string;
    //priority attributes
    priority?: string;
    priorityCalculated?: string;
    priorityJustification?: string;
    focusAreas?: string;
    ignitionHistory?: string;
    sfppProximity?: string;
    residentialProximity?: string;
    priorityJustificationHistory?: string;
}
